// shared/calc/money.ts
/** All money inside shared/calc is INTEGER CENTS (spec §5). One rounding
 *  function; Postgres numeric(15,2) string ↔ cents conversion centralised. */
export type Cents = number;

/** Invalid financial input is distinct from an unexpected application error. */
export class CalcInputError extends TypeError {
  readonly code = "INVALID_FINANCIAL_INPUT";
  constructor(message: string) {
    super(message);
    this.name = "CalcInputError";
  }
}

/** Domain bound: |dollars| ≤ 1e9 ($1B) ⇒ |cents| ≤ 1e11, where the drift guard
 *  below stays ≤ 0.1001 cents — far below half a cent, far above double ulp.
 *  Brokerage figures never approach this; the bound makes the safety explicit. */
const MAX_CENTS = 1e11;

/** Accept decimal notation only; Number alone also accepts blanks and hex. */
export function parseDecimal(value: number | string): number {
  if (typeof value === "string" && !/^[+-]?(?:\d+(?:\.\d+)?|\.\d+)$/.test(value)) {
    throw new CalcInputError(`Invalid decimal value: ${value}`);
  }
  const number = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(number)) throw new CalcInputError(`Non-finite decimal value: ${value}`);
  return number;
}

export function assertCents(value: number): Cents {
  if (!Number.isSafeInteger(value)) throw new CalcInputError(`Money must be safe integer cents: ${value}`);
  if (Math.abs(value) > MAX_CENTS) throw new CalcInputError(`Money value out of supported range: ${value}`);
  return value;
}

export function roundCents(value: number): Cents {
  if (!Number.isFinite(value)) throw new CalcInputError(`Non-finite money value: ${value}`);
  if (Math.abs(value) > MAX_CENTS) throw new CalcInputError(`Money value out of supported range: ${value}`);
  // half-up with a float-drift guard: 0.015*100 = 1.4999999999999998 must round
  // to 2. Guard = 1e-9 absolute + 1e-12 relative — covers ulp drift at every
  // in-domain magnitude without ever reaching half a cent (≤ 0.1001 at $1B).
  return Math.floor(value + 0.5 + 1e-9 + Math.abs(value) * 1e-12);
}

export function toCents(value: number | string): Cents {
  const n = parseDecimal(value);
  // string path is exact for numeric(15,2); float path guards drift e.g. 0.015*100
  return roundCents(n * 100);
}

export function fromCents(cents: Cents): number {
  return assertCents(cents) / 100;
}

export function sumCents(values: readonly Cents[]): Cents {
  return values.reduce((acc, v) => {
    return assertCents(acc + assertCents(v));
  }, 0);
}

export function guarantorIncomeCents(incomeCents: Cents, percent: number | string | null | undefined): Cents {
  assertCents(incomeCents);
  const contribution = percent == null ? 100 : parseDecimal(percent);
  if (contribution < 0 || contribution > 100) {
    throw new CalcInputError(`Guarantor contribution out of range: ${contribution}`);
  }
  return roundCents(incomeCents * (contribution / 100));
}
